import { captureException } from '@sentry/nextjs';
import * as Sentry from '@sentry/react';
import { useRouter } from 'next/router';

import { useTicketsIdByDatoEventSlug } from 'queries/tickets/useTicketsIdByDatoEventSlug';
import { getErrorCode, RestApiError } from 'services/api/restful/errors';
import type {
  AccountEventTicket,
  BookedObject,
  Event,
  MoneyInfo,
} from 'services/api/restful/generated/nofomoRestApi.schemas';
import { useCartStore, useCartStoreActions } from 'services/stores/cart';

import { DEFAULT_CURRENCY } from './constants';
import type { EnhancedTicketInfo, LocalCartItem } from './types';

export const getTicketPrice = (
  price:
    | Pick<EnhancedTicketInfo, 'originalPrice' | 'discountedPrice'>
    | null
    | undefined,
) => {
  const { originalPrice, discountedPrice } = price ?? {};
  const calculatedDiscountedPrice = !!discountedPrice
    ? discountedPrice
    : originalPrice;
  const calculatedOriginalPrice =
    !!discountedPrice &&
    !!originalPrice &&
    originalPrice.amount > discountedPrice.amount
      ? originalPrice
      : undefined;

  return {
    originalPrice: calculatedOriginalPrice,
    discountedPrice: calculatedDiscountedPrice,
  };
};

export const getTicketAndExtraTicketIds = (event: Event | undefined) =>
  Array.from(
    event?.tickets?.reduce<Set<string>>((memo, ticket) => {
      memo.add(ticket.id);
      ticket.extraItems?.forEach((extraItem) => memo.add(extraItem.id));
      return memo;
    }, new Set()) ?? [],
  );

export const hasSeatedItems = (items: Array<LocalCartItem>) =>
  items.some((item) => isSeatedItem(item?.bookObjectsInfo));

export const isSeatedItem = (
  bookedObjectInfo: Array<BookedObject> | undefined,
) => !!bookedObjectInfo?.length;

export const getItemCount = (items?: Array<{ quantity: number }>) =>
  items?.reduce((memo, item) => memo + (item.quantity ?? 0), 0) ?? 0;

export const getAllItemsCount = (items?: Array<LocalCartItem>) =>
  getItemCount(items);

interface TicketBarcodePdf extends AccountEventTicket {
  scannable: {
    __typename: 'TicketBarcodePdf';
    pdfTicketId: string;
  };
}

export const isPdfTicket = (
  ticket: Pick<AccountEventTicket, 'scannable'>,
): ticket is Pick<AccountEventTicket, 'scannable'> & TicketBarcodePdf =>
  ticket.scannable.__typename === 'TicketBarcodePdf' &&
  !!ticket.scannable.pdfTicketId;

export const toMoneyInfo = (
  amount: MoneyInfo | number | undefined,
  currency: string = DEFAULT_CURRENCY,
) =>
  typeof amount === 'number'
    ? {
        amount,
        currency,
      }
    : amount;

export const toTotalPrice = (
  price: MoneyInfo | undefined,
  quantity: number,
) => ({
  amount: (price?.amount ?? 0) * quantity,
  currency: price?.currency ?? DEFAULT_CURRENCY,
});

export const getEventKey = (eventId: string, timeslotId: string | undefined) =>
  [eventId, timeslotId].filter((t) => !!t && !!t.length).join('-');

export const getSubTicketIcon = (ticket: { ticketType?: string | null }) => {
  switch (ticket.ticketType) {
    case 'Parking':
      return 'Parking';
    case 'CreditWallet':
      return 'Wallet';
    case 'EventAccess':
      return 'Ticket';
    default:
      return undefined;
  }
};

/**
 * Appends locale as a query parameter to external ticket URLs.
 *
 * @param url - The external URL to append locale to
 * @param context - Context information for error reporting (service name and function)
 * @returns The URL with locale appended, or original URL if error occurs
 */
export const appendLocaleToExternalTicketUrl = (
  url: string | null | undefined,
  locale: string,
  context: { service: string; func: string },
): string | null | undefined => {
  if (!url) {
    return url;
  }

  try {
    const urlObj = new URL(url);
    if (locale) urlObj.searchParams.set('language', locale);

    return urlObj.toString();
  } catch (error) {
    captureException(error as Error, {
      extra: {
        tags: {
          url,
          service: context.service,
          func: context.func,
        },
      },
    });
    return url;
  }
};

export const useTicketingCartId = () => {
  const cartId = useCartStore((state) => state.ticketingCartId);
  const { setTicketingCartId } = useCartStoreActions();
  return {
    cartId: cartId ?? '',
    setCartId: setTicketingCartId,
  };
};

export const useTicketingEventId = () => {
  const { query } = useRouter();
  const slug = query.slug?.toString() ?? '';

  const { ticketsEventId, ticketsTimeslotId } = useTicketsIdByDatoEventSlug({
    slug,
  });

  return { eventId: ticketsEventId, timeslotId: ticketsTimeslotId };
};

export const intlIdForTicketingError = (
  page: 'cart' | 'coupon' | 'order',
  errors?: RestApiError,
) => {
  const intlBase = `tickets.${page}`;
  const errorCode = errors ? getErrorCode(errors) : undefined;

  if (!!errorCode) {
    switch (errorCode) {
      case 'inventory_not_enough_stock':
      case 'inventory-00000003':
      case 'inventory_out_of_stock':
      case 'inventory-00000002':
      case 'inventory_missing_channel_inventories':
      case 'inventory_missing_ticket_inventories':
        return `${intlBase}.inventory_not_enough_stock.label`;
      case 'sales_wrong_payment_method':
        return `${intlBase}.error.sales_wrong_payment_method`;
      case 'inventory_error':
        return `${intlBase}.inventory_error.label`;
      case 'CouponRecipientDoesNotMatch':
        return `${intlBase}.couponRecipientDoesNotMatch.label`;
      case 'CouponLimitReached':
        return `${intlBase}.couponLimitReached.label`;
      case 'sales-00000020':
        return `${intlBase}.error.apple_pay_insufficient_funds.label`;
    }
  }

  if (page === 'coupon') {
    return `tickets.checkout.apply.coupon.error`;
  }

  if (!!errors) {
    Sentry.captureException(errors);
  }

  return `${intlBase}.generic.error.label`;
};
